import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CachedCustomerService } from '@src/services/cached-customer.service';
import { ModalService } from '@src/shared/modal/modal.service';
import { NewAdHocDiscountModalComponent } from '@src/shared/modal/new-adhoc-discount-modal/new-adhoc-discount-modal.component';
import { NewDiscountModalComponent } from '@src/shared/modal/new-discount-modal/new-discount-modal.component';
import { scrollTo } from '../../../utils/dom';
import formatDistance from '../../../utils/formatDistance';
import { UserRole } from '../../../utils/user-roles';
import { AdHocContract, AdHocContractParams } from '../../../woo_services.module/AdHocContractService';
import { AdOrganisationService } from '../../../woo_services.module/AdOrganisationService';
import { AuthService } from '../../../woo_services.module/AuthService';
import { CustomerService } from '../../../woo_services.module/CustomerService';
import { DiscountsInteractionService } from '../../../woo_services.module/DiscountsInteractionService';
import { RoutingService } from '../../../woo_services.module/RoutingService';
import { TranslationService } from '../../../woo_services.module/TranslationService';
import { UserParams, UserService } from '../../../woo_services.module/UserService';
import { VoucherParams, VoucherService } from '../../../woo_services.module/VoucherService';
import {
  AgencyType,
  Brand,
  Customer as CustomerType,
  HistoryItem,
  Voucher,
  wooId,
} from '../../../woo_services.module/shared-types';

@Component({
  selector: 'customer',
  templateUrl: './customer.component.html',
  styleUrls: ['./customer.component.scss'],
  standalone: false,
})
export class Customer implements OnInit {
  readonly changeDetection = inject(ChangeDetectorRef);
  voucher;
  readonly authService = inject(AuthService);
  private readonly cachedCustomerService = inject(CachedCustomerService);
  readonly translationService = inject(TranslationService);
  readonly isAdminOrPlanner: boolean = this.authService.adminOrPlanner();
  readonly isAdminPlannerAccounting: boolean = this.authService.adminPlannerOrAccounting();
  readonly mayBrowseAsCashCustomer: boolean = this.authService.hasAnyRole([
    UserRole.admin,
    UserRole.sales,
    UserRole.salesManager,
  ]);
  readonly mayCreateDiscount: boolean = this.authService.hasAnyRole([
    UserRole.admin,
    UserRole.planner,
    UserRole.externalPlanner,
    UserRole.sales,
    UserRole.salesManager,
  ]);
  readonly mayCreateNewUser: boolean = !this.authService.hasAnyRole([
    UserRole.sales,
    UserRole.salesManager,
    UserRole.client,
  ]);
  readonly mayViewHistory: boolean = this.isAdminOrPlanner;

  customerType: CashOrDirect = CashOrDirect.direct;

  cramUpdated: string;
  cramSynced: string;
  customerHistory: HistoryItem[];
  activeBrands: Brand[];
  inactiveBrands: Brand[];
  forms: any;
  convert = this.translationService.convertDiscount;
  organisations = [];
  newUserObj = {
    first_name: '',
    last_name: '',
    email: '',
  };
  customer: CustomerType;
  show = {
    newUser: false,
    endedDiscounts: false,
    loadingCustomers: true,
    updatingCustomerType: false,
    newAdHocContract: false,
    newVoucher: false,
  };

  @ViewChild('newItemInCustomer', { static: true }) newItemInCustomer: ElementRef;

  constructor(
    private voucherService: VoucherService,
    private adOrganisationService: AdOrganisationService,
    private customerService: CustomerService,
    private discountsService: DiscountsInteractionService,
    private route: ActivatedRoute,
    private routingService: RoutingService,
    private userService: UserService,
    private modalService: ModalService,
  ) {}

  async ngOnInit(): Promise<void> {
    const id = this.routingService.getParam(this.route, 'id');
    await this.loadCustomer(id);
    await this.loadHistory(id);
  }

  private async loadHistory(customerId) {
    this.customerHistory = await this.customerService.getCustomerHistory(customerId);
  }

  private async loadCustomer(customerId) {
    this.show.loadingCustomers = true;

    try {
      this.setCustomer(await this.customerService.getCustomer(customerId));
    } finally {
      this.show.loadingCustomers = false;
    }
  }

  setCustomer(customer: CustomerType): CustomerType {
    this.customer = customer;
    this.customerType = customer.cash_customer ? CashOrDirect.cash : CashOrDirect.direct;
    this.cramUpdated = formatDistance(new Date(this.customer.crm_last_modified_at));
    this.cramSynced = formatDistance(new Date(this.customer.crm_last_synced_at));
    this.activeBrands = customer.brands.filter((brand) => brand.active);
    this.inactiveBrands = customer.brands.filter((brand) => !brand.active);
    return customer;
  }

  getCustomerType(): string {
    if (this.customer.cash_customer) return 'Cashkund';
    if (!this.customer.agencies.length) return 'Direktkund';

    return 'Byråkund';
  }

  openNewUser(): void {
    this.show.newUser = true;
    if (this.newItemInCustomer) {
      scrollTo(this.newItemInCustomer.nativeElement);
    }
  }

  closeNewUser(): void {
    this.show.newUser = false;
  }

  async addUser(user: UserParams, id: wooId, cashCustomer: boolean): Promise<void> {
    user.customer_id = id;
    const role = cashCustomer ? UserRole.cashUser : UserRole.client;

    try {
      this.customer.users.push(await this.userService.createUser(user, role));
      this.closeNewUser();
    } catch (reason) {
      // Send error to sentry
      console.error('Failed to add user: ', reason);
    }
  }

  onVoucherDestroy = async (voucher: Voucher): Promise<void> => {
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: ['Är du säker på att du vill ta bort detta rabatt?'],
        confirmLabel: 'Ta bort',
        cancelLabel: 'Avbryt',
      })
      .then(async (response) => {
        if (response.confirm) {
          this.discountsService
            .deleteVoucher(voucher.id)
            .then(() => {
              this.customer.vouchers = this.customer.vouchers.filter((v) => v.id !== voucher.id);
              this.cachedCustomerService.clear(this.customer.id);
              this.modalService.openSuccess('Wooho! Det där gick ju bra.', 'Rabatten är nu borttagen.');
            })
            .catch((err) => {
              this.modalService.openError(
                'Det gick inte att ta bort rabatten.',
                this.translationService.convertError(err.error.error),
              );
            })
            .finally(() => {
              this.changeDetection.markForCheck();
            });
        }
      });
  };

  onAdHocContractDestroy = async (contract: AdHocContract): Promise<void> => {
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: ['Är du säker på att du vill ta bort detta rabatt?'],
        confirmLabel: 'Ta bort',
        cancelLabel: 'Avbryt',
      })
      .then(async (response) => {
        if (response.confirm) {
          this.discountsService
            .deleteAdHocContract(contract.id)
            .then(() => {
              this.customer.ad_hoc_contracts = this.customer.ad_hoc_contracts.filter((d) => d.id !== contract.id);
              this.cachedCustomerService.clear(this.customer.id);
              this.modalService.openSuccess('Wooho! Det där gick ju bra.', 'Rabatten är nu borttagen.');
            })
            .catch((err) => {
              this.modalService.openError(
                'Det gick inte att ta bort rabatten.',
                this.translationService.convertError(err.error.error),
              );
            })
            .finally(() => {
              this.changeDetection.markForCheck();
            });
        }
      });
  };

  openNewVoucher = async (): Promise<void> => {
    this.adOrganisationService.getAdOrganisations().then((adOrgs) => {
      const ref = this.modalService.openCustom(NewDiscountModalComponent, {
        agencies: this.customer.agencies,
        adOrganisations: adOrgs,
      });

      ref.componentInstance!.submit.subscribe((value: VoucherParams) => {
        this.voucherService
          .createVoucher(this.customer.id, value)
          .then((newVoucher: Voucher) => {
            this.modalService.openSuccess('Wooho! Det där gick ju bra.', 'Rabatten är nu tillagd.');
            this.customer.vouchers = this.customer.vouchers.concat([newVoucher]);
            this.cachedCustomerService.clear(this.customer.id);
          })
          .catch((err) => {
            this.modalService.openError(
              'Det gick inte att skapa rabatten.',
              this.translationService.convertError(err.error.error),
            );
          })
          .finally(() => {
            this.changeDetection.markForCheck();
            ref.close();
          });
      });
    });
  };

  openNewAdHocContract = async (): Promise<void> => {
    this.adOrganisationService.getAdOrganisations().then((adOrgs) => {
      const ref = this.modalService.openCustom(NewAdHocDiscountModalComponent, {
        agencies: this.customer.agencies,
        adOrganisations: adOrgs,
      });

      ref.componentInstance!.submit.subscribe((contract: AdHocContractParams) => {
        this.discountsService
          .createAdHocContract(contract, this.customer.id)
          .then((newAdHocContract: AdHocContract) => {
            this.customer.ad_hoc_contracts = this.customer.ad_hoc_contracts.concat([newAdHocContract]);
            this.cachedCustomerService.clear(this.customer.id);
          })
          .catch((err) => {
            this.modalService.openError(
              'Det gick inte att skapa Ad Hoc-rabatten.',
              this.translationService.convertError(err.error.error),
            );
          })
          .finally(() => {
            this.changeDetection.markForCheck();
            ref.close();
          });
      });
    });
  };

  otherAgency(agencyType: AgencyType): boolean {
    return (
      agencyType !== AgencyType.mediaAgency &&
      agencyType !== AgencyType.digitalMediaAgencyWithDiscount &&
      agencyType !== AgencyType.partner
    );
  }
}

enum CashOrDirect {
  cash = 'cash',
  direct = 'direct',
}
